// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import LaborReportFilters from 'OnLabor/report/labor-report-filters';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import Panel from 'Ui/components/panel/panel';
import ScheduleStatusDataTable from 'OnLabor/report/schedule-status-data-table';
import WorkboardBarChart from 'OnLabor/report/workboard-bar-chart';
import WorkboardDataTable from 'OnLabor/report/workboard-data-table';
import WorkboardLineChart from 'OnLabor/report/workboard-line-chart';
import WorkboardPieChart from 'OnLabor/report/workboard-pie-chart';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getTileData} from 'Services/panel-service';
import {fetchEffectData} from 'Utils/react-utils';
import {SCHEDULE_STATUS, WORKBOARDS} from 'Ui/components/graph/constants/graph-filters';
import {BY_STATUS, BY_TIME, BY_USER_STATUS, ID_TO_PROPERTY_MAP} from 'OnLabor/report/constants/labor-report';
import {COURSE} from 'Common/constants/membership-type';
import {PRO_GOLF_LABOR_REPORTS_1} from 'Common/constants/isg-toggles';
import {clsx} from 'clsx';
import moment from 'moment';
import {sortBy} from 'lodash';
import Typography from '@mui/material/Typography';
import NoDataMessage from 'Ui/components/common/message/no-data-message';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {IconWdt} from '@deere/icons';
import {exportLaborByEmployeeDetailReport, exportLaborByStatusDetailReport} from 'Services/excel-service';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {
    getLaborByEmployeeExportContent,
    getLaborByStatusExportContent
} from 'Utils/labor-report-export-utils';

const LOADING_ICON_PROPS = {
    className: 'dashboard-loading-icon',
    size: '50px'
};

function initializeStateAndRefs() {
    const [allData, setAllData] = React.useState(() => {
        const startDate = moment().startOf('month');
        const endDate = moment().endOf('month');

        return {
            dateSelect: startDate.format('YYYYMMDD'),
            endDate: endDate.format('YYYYMMDD'),
            laborReportData: {
                users: []
            },
            startDate: startDate.format('YYYYMMDD')
        };
    });
    const [dateRangeSelection, setDateRangeSelection] = React.useState(
        () => [moment().startOf('month').toDate(), moment().endOf('month').toDate()]
    );
    const [loading, setLoading] = React.useState({
        dateFilter: true,
        primarySelector: true
    });
    const [primarySelector, setPrimarySelector] = React.useState(WORKBOARDS);
    const [secondarySelector, setSecondarySelector] = React.useState(null);
    const [tertiarySelector, setTertiarySelector] = React.useState(null);
    const [titleDate, setTitleDate] = React.useState(null);

    return {
        allData,
        dateRangeSelection,
        loading,
        primarySelector,
        secondarySelector,
        tertiarySelector,
        timeScale: React.useRef('day'),
        titleDate,
        setAllData,
        setDateRangeSelection,
        setLoading,
        setPrimarySelector,
        setSecondarySelector,
        setTertiarySelector,
        setTitleDate
    };
}

function aggregateDaysByStatusForUsers(users, initialStatuses, aggregateFunc) {
    return users.reduce((userMap, user) => {
        const {
            name,
            optionMap = {},
            ...otherProps
        } = user;

        if (!userMap.has(name)) {
            userMap.set(name, {
                ...otherProps,
                name,
                optionMap: {
                    ...initialStatuses
                }
            });
        }

        const userFromMap = userMap.get(name);

        Object.keys(optionMap).forEach((status) => {
            if (!userFromMap.optionMap[status]) {
                userFromMap.optionMap[status] = 0;
            }

            aggregateFunc(userFromMap, status, optionMap[status], optionMap);
        });

        return userMap;
    }, new Map());
}

function getScheduleStatusAvailableDays({
    endDate,
    startDate
}) {
    return moment(endDate, 'YYYYMMDD').diff(moment(startDate, 'YYYYMMDD'), 'days') + 1;
}

function getAggregatedDaysByStatusForUsers(allData) {
    const availableDays = getScheduleStatusAvailableDays(allData);

    const initialStatuses = {
        Available: availableDays
    };

    return aggregateDaysByStatusForUsers(allData.laborReportData.users, initialStatuses, (user, status, daysForStatus, optionMap) => {
        if (status === 'Available') {
            return;
        }

        user.optionMap[status] += daysForStatus;

        // Each row's own Available flag (0 or 1) tells us whether that specific day/period
        // should reduce the calendar-seeded total - a tag like "Late" can appear alongside
        // Available:1, meaning the day still counts as available and must not be subtracted.
        if (!optionMap.Available) {
            user.optionMap.Available -= daysForStatus;
        }
    });
}

function getLaborDataForTable({
    allData,
    membership,
    primarySelector,
    secondarySelector
}) {
    const {laborReportData} = allData;

    if (primarySelector === SCHEDULE_STATUS) {
        const aggregatedDaysByStatus = getAggregatedDaysByStatusForUsers(allData);

        return Array.from(aggregatedDaysByStatus.values());
    }

    if (membership.membershipType === COURSE) {
        return laborReportData[ID_TO_PROPERTY_MAP[secondarySelector]] || [];
    }

    return laborReportData.totals ? [laborReportData.totals] : [];
}

function sortData({name}) {
    return name?.toLowerCase()?.trim();
}

function getLaborData({
    allData,
    membership,
    primarySelector,
    secondarySelector
}) {
    const {laborReportData} = allData;

    const laborDataForTable = getLaborDataForTable({
        allData,
        membership,
        primarySelector,
        secondarySelector
    });

    const sortedLaborDataForTable = sortBy(laborDataForTable, sortData);

    const laborDataForChart = secondarySelector === BY_TIME ? sortBy(laborReportData.users, sortData) : sortedLaborDataForTable;

    return [laborDataForChart, sortedLaborDataForTable];
}

function hasData(data) {
    return data?.length > 0;
}

function getChartComponent(primarySelector, secondarySelector) {
    if (primarySelector === WORKBOARDS && secondarySelector === BY_TIME) {
        return WorkboardLineChart;
    }

    if (primarySelector === SCHEDULE_STATUS && secondarySelector === BY_USER_STATUS) {
        return WorkboardPieChart;
    }

    return WorkboardBarChart;
}

function getDataTableComponent(primarySelector) {
    return primarySelector === WORKBOARDS ? WorkboardDataTable : ScheduleStatusDataTable;
}

function resolveRangeTimeScale(startDate, endDate) {
    const start = moment(startDate, 'YYYYMMDD');
    const end = moment(endDate, 'YYYYMMDD');

    if (start.isSame(end, 'day')) {
        return 'day';
    }

    const isFullYear = start.isSame(end, 'year') &&
        start.format('YYYYMMDD') === start.clone().startOf('year').format('YYYYMMDD') &&
        end.format('YYYYMMDD') === end.clone().endOf('year').format('YYYYMMDD');

    if (isFullYear) {
        return 'year';
    }

    // Multi-day ranges default to month scale so the chart uses day-level x-axis ticks instead of minute-level.
    return 'month';
}

function getUpdateDateFilterFunc({
    allData,
    membership,
    primarySelector,
    setAllData,
    setLoading,
    setTitleDate,
    timeScale
}) {
    const updateDateRangeFilter = React.useCallback(async (startDate, endDate, isMounted = () => true) => {
        setLoading((prevLoading) => ({
            ...prevLoading,
            dateFilter: true
        }));

        const scale = resolveRangeTimeScale(startDate, endDate);
        const laborReportData = await getTileData(primarySelector, {
            end: endDate,
            start: startDate
        });

        if (isMounted()) {
            timeScale.current = scale;

            const titleDate = `${moment(startDate, 'YYYYMMDD').format('MMM D, YYYY')} - ${moment(endDate, 'YYYYMMDD').format('MMM D, YYYY')}`;

            setTitleDate(titleDate);

            setAllData((prevData) => ({
                ...prevData,
                dateSelect: startDate,
                endDate,
                laborReportData,
                startDate
            }));

            setLoading({
                dateFilter: false,
                primarySelector: false
            });
        }
    }, [primarySelector]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading((prevLoading) => ({
            ...prevLoading,
            primarySelector: true
        }));
        await updateDateRangeFilter(allData.startDate, allData.endDate, isMounted);
    }), [membership.membershipId, primarySelector]);

    return {
        updateDateRangeFilter
    };
}

function useLaborReportExport({
    addToast,
    allData,
    laborDataForTable,
    secondarySelector,
    tertiarySelector,
    timeScale,
    isExportPreviewEnabled,
    translations
}) {
    const [showExportDialog, setShowExportDialog] = React.useState(false);
    const exportParams = {
        dateSelect: allData.dateSelect,
        laborData: laborDataForTable,
        tertiarySelector,
        timeScale: timeScale.current,
        translations
    };
    const exportContent = React.useMemo(() => {
        if (secondarySelector === BY_STATUS) {
            return getLaborByStatusExportContent(exportParams);
        }

        if (secondarySelector === BY_USER_STATUS) {
            return getLaborByEmployeeExportContent(exportParams);
        }

        return null;
    }, [allData.dateSelect, allData.endDate, laborDataForTable, secondarySelector, tertiarySelector, timeScale.current, translations]);

    const handleClose = () => setShowExportDialog(false);
    const handleDownload = React.useCallback(async () => {
        const exportArgs = {
            laborData: laborDataForTable,
            timeScale: timeScale.current,
            dateSelect: allData.dateSelect,
            startDate: allData.startDate,
            endDate: allData.endDate,
            translations,
            tertiarySelector,
            addToast,
            formatNumbers: true
        };

        if (secondarySelector === BY_STATUS) {
            await exportLaborByStatusDetailReport(exportArgs);
        } else {
            await exportLaborByEmployeeDetailReport(exportArgs);
        }

        setShowExportDialog(false);
    }, [secondarySelector, laborDataForTable, timeScale, allData, translations, tertiarySelector, addToast]);
    const handleOpen = React.useCallback(async () => {
        if (isExportPreviewEnabled) {
            setShowExportDialog(true);
        } else {
            await handleDownload();
        }
    }, [handleDownload, isExportPreviewEnabled]);

    return {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        showExportDialog
    };
}

function LaborReport(props) {
    const {
        membership,
        isExportPreviewEnabled,
        translations
    } = props;

    const {
        allData,
        dateRangeSelection,
        loading,
        primarySelector,
        secondarySelector,
        tertiarySelector,
        timeScale,
        titleDate,
        setAllData,
        setDateRangeSelection,
        setLoading,
        setPrimarySelector,
        setSecondarySelector,
        setTertiarySelector,
        setTitleDate
    } = initializeStateAndRefs();

    const {addToast} = useToast();

    const [isTimeAxis, ChartComponent, DataTableComponent] = React.useMemo(() => [
        secondarySelector?.includes(BY_TIME),
        getChartComponent(primarySelector, secondarySelector),
        getDataTableComponent(primarySelector)
    ], [primarySelector, secondarySelector]);

    const {updateDateRangeFilter} = getUpdateDateFilterFunc({
        allData,
        membership,
        primarySelector,
        setAllData,
        setLoading,
        setTitleDate,
        timeScale
    });

    const updateDateRange = React.useCallback((newDates) => {
        const [startValue, endValue] = newDates;
        const hasRange = Boolean(startValue) && Boolean(endValue);
        const resolvedDates = hasRange ?
            newDates :
            [moment().startOf('month').toDate(), moment().endOf('month').toDate()];

        setDateRangeSelection(resolvedDates);

        return updateDateRangeFilter(moment(resolvedDates[0]).format('YYYYMMDD'), moment(resolvedDates[1]).format('YYYYMMDD'));
    }, [setDateRangeSelection, updateDateRangeFilter]);

    const [laborDataForChart, laborDataForTable] = getLaborData({
        allData,
        membership,
        primarySelector,
        secondarySelector
    });

    const className = clsx('graph-panel-wrapper', {
        hidden: loading.dateFilter || loading.primarySelector
    });

    const hasLaborDataForChart = hasData(laborDataForChart);
    const hasLaborDataForTable = hasData(laborDataForTable);
    const byStatus = secondarySelector === BY_STATUS || secondarySelector === BY_USER_STATUS;

    const {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        showExportDialog
    } = useLaborReportExport({
        addToast,
        allData,
        laborDataForTable,
        secondarySelector,
        tertiarySelector,
        timeScale,
        isExportPreviewEnabled,
        translations
    });

    useNavBarActions(byStatus && hasLaborDataForTable ?
        [{
            disabled: loading.dateFilter || loading.primarySelector,
            Icon: <IconWdt/>,
            onClick: handleOpen,
            title: 'EXPORT',
            variant: 'primary'
        }] : []);

    return (
        <LoadingWrapper
            {...LOADING_ICON_PROPS}
            alwaysRenderChildren={true}
            loading={loading.dateFilter || loading.primarySelector}
        >
            <Panel
                className={className}
                translations={translations}
            >
                <LaborReportFilters
                    dateRangeValue={dateRangeSelection}
                    laborReportData={laborDataForChart}
                    primarySelector={primarySelector}
                    secondarySelector={secondarySelector}
                    tertiarySelector={tertiarySelector}
                    translations={translations}
                    updateDateRange={updateDateRange}
                    updatePrimarySelector={(selected) => {
                        if (selected !== primarySelector) {
                            setLoading((prevLoading) => ({
                                ...prevLoading,
                                primarySelector: true
                            }));
                        }

                        setPrimarySelector(selected);
                    }}
                    updateSecondarySelector={setSecondarySelector}
                    updateTertiarySelector={setTertiarySelector}
                />
                <Typography
                    textAlign='center'
                    variant='h2'
                >{`${membership.name} ${translations.ONLINK_LABOR_REPORT} - ${titleDate}`}</Typography>
                <NoDataMessage
                    hasData={hasLaborDataForChart || hasLaborDataForTable}
                    noDataMessage={translations.ONLINK_NO_DATA_GRAPH}
                >
                    {
                        hasLaborDataForChart &&
                        <div className='chart-container'>
                            <ChartComponent
                                chartDataForSelector={laborDataForChart}
                                isTimeAxis={isTimeAxis}
                                membership={membership}
                                primarySelector={primarySelector}
                                secondarySelector={secondarySelector}
                                tertiarySelector={tertiarySelector}
                                timeScale={timeScale.current}
                                translations={translations}
                            />
                        </div>
                    }
                    {
                        hasLaborDataForTable &&
                        <DataTableComponent
                            dateSelector={allData.dateSelect}
                            endDate={allData.endDate}
                            isExportPreviewEnabled={isExportPreviewEnabled}
                            isTimeAxis={isTimeAxis}
                            laborReportData={laborDataForTable}
                            membership={membership}
                            primarySelector={primarySelector}
                            secondarySelector={secondarySelector}
                            startDate={allData.startDate}
                            tertiarySelector={tertiarySelector}
                            timeScale={timeScale.current}
                            translations={translations}
                        />
                    }
                </NoDataMessage>
            </Panel>
            {
                showExportDialog && exportContent &&
                <LaborReportExportDialog
                    dateRange={titleDate}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows,
                        exportContent.footer
                    ]}
                    translations={translations}
                />
            }
        </LoadingWrapper>
    );
}

LaborReport.propTypes = {
    isExportPreviewEnabled: PropTypes.bool,
    membership: PropTypes.membership,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isExportPreviewEnabled: Boolean(state.account.isgToggles?.[PRO_GOLF_LABOR_REPORTS_1]),
        membership: state.membership
    };
}

export default connect(mapStateToProps)(LaborReport);
